import { formatDateDDMMYY } from '../../utils/dateFormat';
// src/components/streamer-tabs/SubscriptionTab.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { useAuth } from '../../hooks';
import {
  BillingAccountSummary,
  BillingChannelSummary,
  BillingInvoiceSummary,
  BillingPaymentSummary,
  BillingServiceLine,
  cancelInvoicePaymentAttempt,
  downloadInvoicePdf,
  downloadPaymentHistoryPdf,
  downloadPaymentPdf,
  getBillingAccountSummary,
  initiatePayment,
  retryInvoicePayment,
  sendInvoicePdf,
  updateTrialDeliveryOptions,
} from '../../services/billingService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useRuntimeActions } from '../../contexts/ConsoleContext';
import { navigateToTrustedRedirect } from '../../utils/safeRedirect';

const cardSx = {
  borderRadius: 1,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
} as const;

const sectionHeaderSx = {
  px: 2,
  py: 1.25,
  borderBottom: '0.5px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
} as const;

const labelSx = {
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.07em',
  color: 'text.disabled',
} as const;

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.78rem',
  fontWeight: 500,
} as const;

const smallBtnSx = {
  borderRadius: 1,
  fontSize: '0.75rem',
  textTransform: 'none' as const,
  minWidth: 0,
} as const;

const gatewayLabel = (gateway?: string) => ({ pesapal: 'PesaPal / Card', mpesa: 'M-Pesa', eft: 'EFT', manual: 'Manual' })[String(gateway || '').toLowerCase() as 'pesapal' | 'mpesa' | 'eft' | 'manual'] || 'Manual';

const formatMoney = (currency?: string, amount?: number | null) =>
  `${currency || 'USD'} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const formatDate = (value?: string | null) => {
  return formatDateDDMMYY(value, 'N/A');
};

const invoiceServiceEntries = (invoice: BillingInvoiceSummary) => {
  const unique = new Map<string, string>();
  (invoice.items || []).forEach((item) => {
    const code = String(item.code || item.lineType || item.description || 'SERVICE').trim().toUpperCase();
    const quantity = Number(item.quantity || 1);
    const label = `${item.description || code}${quantity > 1 ? ` x ${quantity}` : ''}`;
    if (!unique.has(code)) unique.set(code, label);
  });
  return Array.from(unique.entries()).map(([code, label]) => ({ code, label }));
};

const InvoiceServices = ({ invoice }: { invoice: BillingInvoiceSummary }) => {
  const services = invoiceServiceEntries(invoice).map((entry) => entry.label);
  const fullLabel = services.join(', ') || 'No service lines';
  const compactLabel = services.length > 2 ? `${services.slice(0, 2).join(', ')} +${services.length - 2}` : fullLabel;
  return <Tooltip title={fullLabel}><Typography sx={{ fontSize: '0.76rem', maxWidth: 260 }} noWrap>{compactLabel}</Typography></Tooltip>;
};

const recurringServiceLabel = (service: BillingServiceLine) => {
  switch (service.type) {
    case 'CHANNEL_BASE':
      return 'Channel';
    case 'HLS_STANDARD':
    case 'HLS_STANDARD_INCLUDED':
      return 'HLS';
    case 'HLS_PLUS':
    case 'HLS_SCTE35':
      return 'HLS+';
    case 'DIRECT_OUTPUT':
      return 'Direct Pass';
    case 'MANAGED_OUTPUT':
    case 'MANAGED_UPGRADE':
      return 'Remapped Output';
    case 'SOCIAL_STREAM':
      return 'Social Stream';
    case 'PLAYKASTER':
      return 'PlayKaster';
    case 'EPG_XMLTV':
      return 'EPG XMLTV';
    case 'EPG_GRACENOTE':
      return 'EPG Gracenote';
    default:
      return service.name || service.type.replace(/_/g, ' ');
  }
};

interface SubscriptionTabProps {
  onOpenStore?: () => void;
}

const statusTone = (status?: string) => {
  const s = String(status || '').toUpperCase();
  if (['ACTIVE', 'TRIAL', 'TRIALING', 'PAID'].includes(s)) return 'success';
  if (['GRACE_PERIOD', 'PAST_DUE', 'PENDING', 'PROCESSING', 'ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(s)) return 'warning';
  if (['TRIAL_EXPIRED', 'DISABLED', 'SUSPENDED', 'FAILED', 'CANCELLED', 'CANCELED', 'VOID', 'EXPIRED', 'REFUNDED'].includes(s)) return 'error';
  return 'default';
};

const StatusChip = ({ status }: { status?: string }) => (
  <Chip label={String(status || 'UNKNOWN').replace(/_/g, ' ')} size="small" color={statusTone(status) as any} variant="outlined" sx={{ borderRadius: 0, fontSize: '0.65rem', fontWeight: 700, height: 22 }} />
);

const MetricCard = ({ label, value, helper }: { label: string; value: React.ReactNode; helper?: React.ReactNode }) => (
  <Box sx={{ ...cardSx, p: 2 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, mt: 0.75 }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>{helper}</Typography>}
  </Box>
);

const UsageLine = ({ label, used, limit }: { label: string; used: number; limit: number }) => {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={{ ...monoSx, fontSize: '0.7rem', color: 'text.secondary' }}>{used}/{limit}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 3, borderRadius: 0, bgcolor: 'action.disabledBackground' }} />
    </Box>
  );
};

const ServiceTypeChip = ({ type }: { type: string }) => {
  const label = type.replace(/_/g, ' ');
  const color = type.includes('MANAGED') ? 'secondary' : type.includes('HLS') ? 'primary' : type.includes('EPG') ? 'warning' : 'default';
  return <Chip label={label} size="small" color={color as any} variant="outlined" sx={{ borderRadius: 0, fontSize: '0.62rem', height: 20 }} />;
};

const ChannelServices = ({
  channel,
  currency,
  canPay,
  paying,
  onPay,
}: {
  channel: BillingChannelSummary;
  currency: string;
  canPay: boolean;
  paying: boolean;
  onPay: (channel: BillingChannelSummary) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={cardSx}>
      <Box sx={sectionHeaderSx}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{channel.name}</Typography>
            <StatusChip status={channel.billingStatus || (channel.isActive ? 'ACTIVE' : 'DISABLED')} />
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
            {channel.usage.configuredOutputs} configured outputs / {channel.usage.activeOutputs} active / Due {formatDate(channel.dueDate)}
          </Typography>
          {String(channel.billingStatus || '').toUpperCase() === 'GRACE_PERIOD' && (
            <Typography sx={{ fontSize: '0.72rem', color: 'warning.main', mt: 0.25, fontWeight: 800 }}>
              {channel.gracePeriodDays || 4}-day grace period / Ends {formatDate(channel.graceEndsAt)}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ ...monoSx, fontWeight: 700 }}>{formatMoney(currency, channel.monthlyAmount)}/mo</Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={paying ? <CircularProgress size={13} /> : <CreditCardIcon />}
            disabled={!canPay || paying || channel.monthlyAmount <= 0}
            onClick={(event) => {
              event.stopPropagation();
              onPay(channel);
            }}
            sx={smallBtnSx}
          >
            {paying ? 'Processing...' : 'Pay for this'}
          </Button>
          <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ borderRadius: 0 }}><ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s' }} /></IconButton>
        </Stack>
      </Box>

      {open && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={1.25}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <UsageLine label="Outputs" used={channel.usage.configuredOutputs} limit={channel.limits.outputs} />
              <UsageLine label="Social Stream" used={channel.usage.socialConfigured} limit={channel.limits.socialStream} />
              <UsageLine label="Remapped" used={channel.usage.managedConfigured} limit={channel.limits.managed} />
              <UsageLine label="HLS" used={channel.usage.hlsConfigured} limit={channel.limits.hls} />
              <UsageLine label="HLS+" used={channel.usage.hlsPlusConfigured} limit={channel.limits.hlsPlus} />
            </Box>

            <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.5fr) 90px 90px 100px', gap: 1, px: 1.25, py: 0.75, bgcolor: 'action.hover' }}>
                {['Service', 'Qty', 'Unit', 'Monthly'].map((h) => <Typography key={h} sx={labelSx}>{h}</Typography>)}
              </Box>
              {channel.services.map((service, idx) => (
                <Box key={`${service.type}-${idx}`} sx={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.5fr) 90px 90px 100px', gap: 1, px: 1.25, py: 0.9, alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }} noWrap>{service.name}</Typography>
                      <ServiceTypeChip type={service.type} />
                    </Stack>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>{service.description || '--'}</Typography>
                  </Box>
                  <Typography sx={monoSx}>{service.quantity}</Typography>
                  <Typography sx={monoSx}>{formatMoney(currency, service.unitPrice)}</Typography>
                  <Typography sx={{ ...monoSx, fontWeight: 800 }}>{formatMoney(currency, service.monthlyAmount)}</Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const BillingStrip = ({
  status,
  nextAmount,
  currency,
  dueDate,
  dueChannelName,
  effectiveBillingDate,
  prorationMultiplier,
  prorationRemainingDays,
  prorationFullPeriodDays,
}: {
  status?: string;
  nextAmount: number;
  currency: string;
  dueDate?: string | null;
  dueChannelName?: string | null;
  effectiveBillingDate?: string | null;
  prorationMultiplier?: number;
  prorationRemainingDays?: number | null;
  prorationFullPeriodDays?: number | null;
}) => {
  const items = [
    { label: 'Status', value: <StatusChip status={status} />, helper: 'Live billing state' },
    { label: 'Next Channel Charge', value: formatMoney(currency, nextAmount), helper: dueDate ? `${dueChannelName || 'Channel'} due ${formatDate(dueDate)}` : 'No channel renewal date' },
    { label: 'First Purchase Date', value: formatDate(effectiveBillingDate), helper: 'Original purchase date' },
  ];

  return (
    <Box sx={{ ...cardSx, px: 2, py: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
        {items.map((item) => (
          <Box key={item.label} sx={{ px: 1.25, py: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
            <Typography sx={labelSx}>{item.label}</Typography>
            <Box sx={{ mt: 0.75, minHeight: 24, display: 'flex', alignItems: 'center' }}>
              {typeof item.value === 'string' ? <Typography sx={{ fontSize: '1rem', fontWeight: 800 }}>{item.value}</Typography> : item.value}
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>{item.helper}</Typography>
          </Box>
        ))}
      </Box>
      {prorationMultiplier && Math.abs(prorationMultiplier - 1) > 0.001 && (
        <Alert severity="info" sx={{ mt: 1.25, borderRadius: 0 }}>
          Add-on purchases in this cycle are prorated to the next billing date. Current cycle charge: {prorationRemainingDays ?? 'N/A'} of {prorationFullPeriodDays ?? 'N/A'} days ({(prorationMultiplier * 100).toFixed(0)}%).
        </Alert>
      )}
    </Box>
  );
};

export const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ onOpenStore }) => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { refreshData } = useRuntimeActions();
  const theme = useTheme();

  const [summary, setSummary] = useState<BillingAccountSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [reactivating, setReactivating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [recurringDetailOpen, setRecurringDetailOpen] = useState(false);
  const [invoiceActionId, setInvoiceActionId] = useState<number | null>(null);
  const [paymentSettling, setPaymentSettling] = useState(false);
  const [paymentSettled, setPaymentSettled] = useState(false);
  const [paymentNoticeVisible, setPaymentNoticeVisible] = useState(false);
  const [trialDeliverySaving, setTrialDeliverySaving] = useState<'direct' | 'social' | 'hls' | null>(null);
  const [payingChannelId, setPayingChannelId] = useState<number | null>(null);
  const [dismissedReviewIds, setDismissedReviewIds] = useState<string[]>([]);
  const [cancelAttemptTarget, setCancelAttemptTarget] = useState<BillingInvoiceSummary | null>(null);
  const [cancelAttemptReason, setCancelAttemptReason] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('ALL');
  const [invoiceServiceFilter, setInvoiceServiceFilter] = useState('ALL');

  const reviewDismissStorageKey = useMemo(
    () => `streamy.billing.dismissed-reviews.${String(user?.id || user?.email || 'account')}`,
    [user?.email, user?.id],
  );
  const loadSummary = useMemo(() => async () => {
    const data = await getBillingAccountSummary();
    setSummary(data);
    return data;
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(reviewDismissStorageKey) || '[]');
      setDismissedReviewIds(Array.isArray(saved) ? saved.map(String) : []);
    } catch {
      setDismissedReviewIds([]);
    }
  }, [reviewDismissStorageKey]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        await loadSummary();
      } catch (e: any) {
        showSnackbar(e.response?.data?.message || 'Failed to load billing summary', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (user) run();
    return () => { mounted = false; };
  }, [loadSummary, showSnackbar, user]);

  const selectedGateway = 'manual' as const;

  const trialChannel = summary?.services.channels[0] || null;
  const isTrialActivation = summary?.account.status === 'TRIAL' && summary.account.setupFeePaid === false;
  const trialDirectEnabled = Boolean(trialChannel?.services.some((service) => service.type === 'DIRECT_OUTPUT'));
  const trialSocialEnabled = Boolean(trialChannel?.services.some((service) => service.type === 'SOCIAL_STREAM'));
  const trialHlsEnabled = Boolean(trialChannel?.services.some((service) => service.type === 'HLS_STANDARD' || service.type === 'HLS_STANDARD_INCLUDED'));
  const outstandingInvoices = useMemo(
    () => (summary?.invoices || []).filter((invoice) =>
      Number(invoice.balanceDue || 0) > 0
      && ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(String(invoice.status || '').toUpperCase())
    ),
    [summary?.invoices],
  );
  const outstandingBalance = useMemo(
    () => outstandingInvoices.reduce((sum, invoice) => sum + Number(invoice.balanceDue || 0), 0),
    [outstandingInvoices],
  );
  const invoiceStatusOptions = useMemo(
    () => Array.from(new Set((summary?.invoices || []).map((invoice) => String(invoice.status || 'UNKNOWN').toUpperCase()))).sort(),
    [summary?.invoices],
  );
  const invoiceServiceOptions = useMemo(() => {
    const options = new Map<string, string>();
    (summary?.invoices || []).forEach((invoice) => invoiceServiceEntries(invoice).forEach(({ code, label }) => {
      if (!options.has(code)) options.set(code, label.replace(/\s+x\s+\d+$/i, ''));
    }));
    return Array.from(options.entries()).map(([code, label]) => ({ code, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [summary?.invoices]);
  const filteredInvoices = useMemo(
    () => (summary?.invoices || []).filter((invoice) => {
      const statusMatches = invoiceStatusFilter === 'ALL' || String(invoice.status || '').toUpperCase() === invoiceStatusFilter;
      const serviceMatches = invoiceServiceFilter === 'ALL' || invoiceServiceEntries(invoice).some(({ code }) => code === invoiceServiceFilter);
      return statusMatches && serviceMatches;
    }),
    [invoiceServiceFilter, invoiceStatusFilter, summary?.invoices],
  );
  const outstandingInvoice = [...outstandingInvoices]
    .filter((invoice) => invoice.canRetry && !invoice.canCancelAttempt)
    .sort((left, right) => {
      const leftDue = new Date(left.dueDate || 0).getTime();
      const rightDue = new Date(right.dueDate || 0).getTime();
      return leftDue - rightDue || left.id - right.id;
    })[0] || null;
  const hasActivePaymentAttempt = outstandingInvoices.some((invoice) => invoice.canCancelAttempt);

  const handleTrialDeliveryToggle = async (type: 'direct' | 'social' | 'hls', enabled: boolean) => {
    if (!trialChannel) return;
    setTrialDeliverySaving(type);
    try {
      await updateTrialDeliveryOptions({
        channelId: trialChannel.id,
        directPass: type === 'direct' ? enabled : trialDirectEnabled,
        socialStream: type === 'social' ? enabled : trialSocialEnabled,
        hlsStandard: type === 'hls' ? enabled : trialHlsEnabled,
      });
      await loadSummary();
      await refreshData();
      const label = type === 'direct' ? 'Direct Pass' : type === 'social' ? 'Social Stream' : 'HLS';
      showSnackbar(`${label} trial option ${enabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to update trial delivery option.', 'error');
    } finally {
      setTrialDeliverySaving(null);
    }
  };

  const handleRenew = async () => {
    setReactivating(true);
    try {
      const response = outstandingInvoice
        ? await retryInvoicePayment(outstandingInvoice.id, { paymentGateway: selectedGateway })
        : await initiatePayment({
          action: 'RENEW',
          targetPlan: summary?.account.role || user?.role,
          paymentGateway: selectedGateway,
        });
      const url = response.redirectUrl || (response as any).data?.redirectUrl;
      if (url) {
        if (!navigateToTrustedRedirect(url)) {
          showSnackbar('Payment redirect was blocked because it was not trusted.', 'error');
        }
      }
      else showSnackbar(response.message || 'Payment initiated.', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Payment initiation failed', 'error');
    } finally {
      setReactivating(false);
    }
  };

  const handleChannelPayment = async (channel: BillingChannelSummary) => {
    setPayingChannelId(channel.id);
    try {
      const response = await initiatePayment({
        action: 'RENEW',
        targetPlan: summary?.account.role || user?.role,
        paymentGateway: selectedGateway,
        options: { channelId: channel.id },
      });
      const url = response.redirectUrl || (response as any).data?.redirectUrl;
      if (url) {
        if (!navigateToTrustedRedirect(url)) showSnackbar('Payment redirect was blocked because it was not trusted.', 'error');
      } else {
        await loadSummary();
        showSnackbar(response.message || `Payment initiated for ${channel.name}.`, 'success');
      }
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || `Could not initiate payment for ${channel.name}.`, 'error');
    } finally {
      setPayingChannelId(null);
    }
  };

  const dismissPurchaseReview = (reviewKey: string) => {
    setDismissedReviewIds((current) => {
      const next = Array.from(new Set([...current, reviewKey]));
      localStorage.setItem(reviewDismissStorageKey, JSON.stringify(next));
      return next;
    });
  };

  const invoiceFilename = (invoice: BillingInvoiceSummary) =>
    `${String(invoice.invoiceNumber || `INV-${invoice.id}`).replace(/[^a-z0-9_.-]+/gi, '_')}.pdf`;

  const handleDownloadInvoice = async (invoice: BillingInvoiceSummary) => {
    try {
      setInvoiceActionId(invoice.id);
      const blob = await downloadInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = invoiceFilename(invoice);
      anchor.click();
      URL.revokeObjectURL(url);
      showSnackbar('Invoice PDF downloaded.', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to download invoice PDF', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const handleSendInvoice = async (invoice: BillingInvoiceSummary) => {
    try {
      setInvoiceActionId(invoice.id);
      const response = await sendInvoicePdf(invoice.id);
      showSnackbar(response.message || 'Invoice PDF sent.', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to send invoice PDF', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const handleRetryInvoice = async (invoice: BillingInvoiceSummary) => {
    try {
      setInvoiceActionId(invoice.id);
      const response = await retryInvoicePayment(invoice.id, {
        paymentGateway: selectedGateway,
      });
      const url = response.redirectUrl || (response as any).data?.redirectUrl;
      if (url) {
        if (!navigateToTrustedRedirect(url)) {
          showSnackbar('Payment redirect was blocked because it was not trusted.', 'error');
        }
      } else {
        showSnackbar(response.message || 'Payment retry initiated.', 'success');
      }
      await loadSummary();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to retry invoice payment', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const handleCancelPaymentAttempt = async () => {
    if (!cancelAttemptTarget || cancelAttemptReason.trim().length < 3) {
      showSnackbar('Enter a reason for cancelling this payment attempt.', 'warning');
      return;
    }
    try {
      setInvoiceActionId(cancelAttemptTarget.id);
      const response = await cancelInvoicePaymentAttempt(cancelAttemptTarget.id, cancelAttemptReason.trim());
      showSnackbar(response.message || 'Payment attempt cancelled.', 'success');
      setCancelAttemptTarget(null);
      setCancelAttemptReason('');
      await loadSummary();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to cancel payment attempt', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const renderInvoiceActions = (invoice: BillingInvoiceSummary) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {invoice.canRetry && (
        <Tooltip title={`Retry payment with ${gatewayLabel(selectedGateway)}`}>
          <span>
            <IconButton size="small" sx={{ borderRadius: 0 }} disabled={invoiceActionId === invoice.id} onClick={() => handleRetryInvoice(invoice)}>
              {invoiceActionId === invoice.id ? <CircularProgress size={14} /> : <AutorenewIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {invoice.canCancelAttempt && (
        <Tooltip title="Cancel active payment attempt">
          <span>
            <IconButton
              size="small"
              color="warning"
              sx={{ borderRadius: 0 }}
              disabled={invoiceActionId === invoice.id}
              onClick={() => {
                setCancelAttemptTarget(invoice);
                setCancelAttemptReason('');
              }}
            >
              <CancelOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Download PDF">
        <span>
          <IconButton size="small" sx={{ borderRadius: 0 }} disabled={invoiceActionId === invoice.id} onClick={() => handleDownloadInvoice(invoice)}>
            {invoiceActionId === invoice.id ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Email PDF">
        <span>
          <IconButton size="small" sx={{ borderRadius: 0 }} disabled={invoiceActionId === invoice.id} onClick={() => handleSendInvoice(invoice)}>
            <EmailIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const handleDownloadPayment = async (payment: BillingPaymentSummary) => {
    try {
      setInvoiceActionId(payment.id);
      const blob = await downloadPaymentPdf(payment.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `payment-${String(payment.id).padStart(6, '0')}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
      showSnackbar('Payment receipt PDF downloaded.', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to download payment PDF', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const handleDownloadPaymentHistory = async () => {
    try {
      setInvoiceActionId(-1);
      const blob = await downloadPaymentHistoryPdf();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'streamy-payment-history.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
      showSnackbar('Payment history PDF downloaded.', 'success');
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to download payment history PDF', 'error');
    } finally {
      setInvoiceActionId(null);
    }
  };

  const paymentSuccess = new URLSearchParams(window.location.search).get('payment') === 'success';

  useEffect(() => {
    if (!paymentSuccess) return;

    let cancelled = false;
    let timeoutId: number | undefined;
    let attempts = 0;

    setPaymentSettling(true);
    setPaymentSettled(false);

    const poll = async () => {
      attempts += 1;
      try {
        const data = await loadSummary();
        if (cancelled) return;

        const hasPendingPayments = (data.payments || []).some((payment) =>
          ['PENDING', 'PROCESSING'].includes(String(payment.status || '').toUpperCase())
        );
        const settled = !hasPendingPayments && !['DISABLED', 'PAST_DUE', 'SUSPENDED'].includes(String(data.account.status || '').toUpperCase());

        if (settled || attempts >= 12) {
          setPaymentSettling(false);
          setPaymentSettled(settled);
          setPaymentNoticeVisible(settled);
          if (settled) {
            const url = new URL(window.location.href);
            url.searchParams.delete('payment');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          }
          return;
        }
      } catch {
        if (cancelled || attempts >= 12) {
          setPaymentSettling(false);
          return;
        }
      }

      if (!cancelled) timeoutId = window.setTimeout(poll, 5000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [loadSummary, paymentSuccess]);

  const invoicePreview = summary?.nextInvoice;
  const currency = invoicePreview?.currency || summary?.account.currency || summary?.plan?.currency || 'USD';
  const invoicePreviewLines = useMemo(
    () => invoicePreview?.lines || invoicePreview?.breakdown?.lines || [],
    [invoicePreview],
  );
  const setupFeeFromLines = useMemo(
    () => invoicePreviewLines
      .filter((line) => ['CHANNEL_SETUP', 'PLAYKASTER_SETUP'].includes(String(line.code || '').toUpperCase()))
      .reduce((sum, line) => sum + Number(line.totalAmount || 0), 0),
    [invoicePreviewLines],
  );
  const oneTimeFromLines = useMemo(
    () => invoicePreviewLines
      .filter((line) => ['CHANNEL_SETUP', 'PLAYKASTER_SETUP', 'BILLING_ANCHOR_CHANGE', 'UPGRADE'].includes(String(line.code || '').toUpperCase()))
      .reduce((sum, line) => sum + Number(line.totalAmount || 0), 0),
    [invoicePreviewLines],
  );
  const nextInvoiceAmount = invoicePreview?.amount || 0;
  const amountDueNow = outstandingBalance > 0 ? outstandingBalance : isTrialActivation ? nextInvoiceAmount : 0;
  const amountDueCurrency = outstandingInvoices[0]?.currency || currency;
  const setupFee = invoicePreview?.breakdown?.setupFee ?? setupFeeFromLines;
  const oneTimeTotal = invoicePreview?.breakdown?.oneTimeTotal ?? oneTimeFromLines;
  const recurring = summary ? Number(summary.services?.monthlyAmount ?? summary.plan?.monthlyCost ?? 0) : Math.max(0, nextInvoiceAmount - oneTimeTotal);
  const prorationMultiplier = invoicePreview?.breakdown?.prorationMultiplier || 1;
  const prorationRemainingDays = invoicePreview?.breakdown?.remainingDays;
  const prorationFullPeriodDays = invoicePreview?.breakdown?.fullPeriodDays;
  const nextDueChannel = useMemo(() => {
    const channels = summary?.services.channels || [];
    return channels
      .filter((channel) => channel.dueDate && !Number.isNaN(new Date(channel.dueDate).getTime()))
      .sort((left, right) => new Date(left.dueDate as string).getTime() - new Date(right.dueDate as string).getTime())[0] || null;
  }, [summary?.services.channels]);
  const trialExpired = ['TRIAL_EXPIRED', 'PAST_DUE', 'DISABLED', 'SUSPENDED'].includes(String(summary?.account.status || '').toUpperCase())
    && summary?.account.setupFeePaid === false
    && Boolean(summary?.account.trialExpiresAt)
    && new Date(summary?.account.trialExpiresAt || '').getTime() <= Date.now();
  const dueDate = summary?.account.status === 'TRIAL' || trialExpired ? summary?.account.trialExpiresAt : nextDueChannel?.dueDate;
  const recurringItems = useMemo(() => {
    const grouped = new Map<string, number>();
    (summary?.services.channels || []).forEach((channel) => {
      channel.services.forEach((service) => {
        const amount = Number(service.monthlyAmount || 0);
        if (!amount) return;
        const label = recurringServiceLabel(service);
        grouped.set(label, (grouped.get(label) || 0) + amount);
      });
    });
    return Array.from(grouped.entries()).map(([label, amount]) => ({ label, amount }));
  }, [summary]);
  const recurringItemizedTotal = useMemo(() => recurringItems.reduce((sum, item) => sum + item.amount, 0), [recurringItems]);
  const visiblePurchaseReviews = (summary?.pendingPurchaseReviews || [])
    .map((review) => ({ ...review, reviewKey: `${review.id}:${review.submittedAt || ''}` }))
    .filter((review) => !dismissedReviewIds.includes(review.reviewKey));

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} thickness={3} /></Box>;
  if (!summary) return <Alert severity="error" sx={{ borderRadius: 0 }}>Billing summary is unavailable.</Alert>;

  return (
    <Box sx={{ maxWidth: 1180 }}>
      <Dialog
        open={paymentNoticeVisible && paymentSettled}
        onClose={() => setPaymentNoticeVisible(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon color="success" />
            <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 800 }}>Payment confirmed</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography sx={{ fontSize: '0.86rem' }}>
              We received your payment and updated your billing status successfully.
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Your paid channel services are now available. The payment receipt is available in Payments and the paid invoice is available in Invoice Details.
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', p: 1.25 }}>
              <Typography sx={labelSx}>Account status</Typography>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, mt: 0.4 }}>
                {String(summary.account.status || 'Active').replace(/_/g, ' ')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPaymentNoticeVisible(false)} sx={smallBtnSx}>Continue</Button>
        </DialogActions>
      </Dialog>
      <Stack spacing={2}>
        {paymentSuccess && paymentSettling && <Alert severity="info" sx={{ borderRadius: 0 }}>Payment callback received. Billing status is being refreshed automatically.</Alert>}
        {paymentNoticeVisible && paymentSettled && <Alert severity="success" onClose={() => setPaymentNoticeVisible(false)} sx={{ borderRadius: 0 }}>Payment confirmed and billing status updated.</Alert>}
        {trialExpired && <Alert severity="warning" sx={{ borderRadius: 0 }}>Your trial ended on {formatDate(summary.account.trialExpiresAt)}. All channel inputs and outputs are stopped until activation payment is confirmed.</Alert>}
        {visiblePurchaseReviews.map((review) => (
          <Alert key={review.reviewKey} severity="info" onClose={() => dismissPurchaseReview(review.reviewKey)} sx={{ borderRadius: 0 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800 }}>Purchase under review</Typography>
            <Typography sx={{ fontSize: '0.76rem' }}>
              {review.purchasedItems} via {gatewayLabel(review.gateway)} is awaiting billing approval. Please await the approval or rejection email; review normally completes within one hour.
            </Typography>
          </Alert>
        ))}
        {summary.warnings.length > 0 && <Stack spacing={1}>{summary.warnings.map((warning) => <Alert key={warning} severity="warning" sx={{ borderRadius: 0 }}>{warning}</Alert>)}</Stack>}
        <Box sx={{ ...cardSx, borderColor: statusTone(summary.account.status) === 'error' ? 'error.main' : 'divider' }}>
          <Box sx={sectionHeaderSx}>
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>Billing overview</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Current charges, renewal cost, and the next channel due date.</Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography sx={labelSx}>{summary.plan?.label || summary.account.role}</Typography>
              <StatusChip status={summary.account.status} />
              {summary.account.status === 'TRIAL' && summary.account.trialExpiresAt && (
                <Chip icon={<AccessTimeIcon />} label={`Trial ends ${formatDate(summary.account.trialExpiresAt)}`} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
              )}
              {trialExpired && summary.account.trialExpiresAt && (
                <Chip icon={<AccessTimeIcon />} label={`Trial expired ${formatDate(summary.account.trialExpiresAt)}`} size="small" color="warning" variant="outlined" sx={{ borderRadius: 1 }} />
              )}
            </Stack>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
            <Box sx={{ px: 2, py: 1.75, borderRight: { lg: '1px solid' }, borderBottom: { xs: '1px solid', lg: 0 }, borderColor: 'divider' }}>
              <Typography sx={labelSx}>{isTrialActivation ? 'Activation estimate' : 'Outstanding balance'}</Typography>
              <Typography sx={{ fontSize: '1.45rem', fontWeight: 900, mt: 0.6 }}>{formatMoney(amountDueCurrency, amountDueNow)}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.35 }}>
                {outstandingInvoices.length > 0
                  ? `${outstandingInvoices.length} unpaid invoice${outstandingInvoices.length === 1 ? '' : 's'}`
                  : isTrialActivation ? 'Estimated activation invoice' : 'No unpaid invoices'}
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.75, borderRight: { lg: '1px solid' }, borderBottom: { xs: '1px solid', lg: 0 }, borderColor: 'divider' }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={labelSx}>Monthly recurring</Typography>
                <IconButton size="small" aria-label="View recurring billing summary" onClick={() => setRecurringDetailOpen(true)} sx={{ width: 22, height: 22, color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Stack>
              <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, mt: 0.5 }}>{formatMoney(currency, recurring)}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.35 }}>Services currently retained each month</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.75, borderRight: { lg: '1px solid' }, borderBottom: { xs: '1px solid', sm: 0 }, borderColor: 'divider' }}>
              <Typography sx={labelSx}>Next channel due</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, mt: 0.65 }}>{formatDate(dueDate)}</Typography>
              <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.35 }}>
                {summary.account.status === 'TRIAL' || trialExpired ? 'Trial activation deadline' : nextDueChannel?.name || 'No scheduled renewal'}
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1.75 }}>
              <Typography sx={labelSx}>Next invoice preview</Typography>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, mt: 0.65 }}>{formatMoney(currency, nextInvoiceAmount)}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.35 }}>
                {setupFee > 0 ? `Includes one-time setup fee of ${formatMoney(currency, setupFee)}` : 'Based on currently retained services'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  color={statusTone(summary.account.status) === 'error' ? 'error' : 'primary'}
                  startIcon={reactivating ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
                  disabled={reactivating || hasActivePaymentAttempt}
                  onClick={handleRenew}
                  sx={{ ...smallBtnSx, fontWeight: 800 }}
                >
                  {reactivating
                    ? 'Processing...'
                    : hasActivePaymentAttempt
                      ? 'Payment pending'
                      : outstandingBalance > 0
                        ? outstandingInvoices.length > 1 ? 'Pay next invoice' : 'Pay invoice'
                        : statusTone(summary.account.status) === 'error'
                          ? 'Pay and restore'
                          : isTrialActivation ? 'Activate channel' : 'Pay renewal'}
                </Button>
                <Button variant="outlined" onClick={() => setInvoiceOpen(true)} sx={smallBtnSx} startIcon={<ReceiptLongIcon />}>Invoices</Button>
                <Button variant="outlined" size="small" onClick={() => setHistoryOpen(true)} sx={smallBtnSx} startIcon={<AccountBalanceWalletIcon />}>Payments</Button>
              </Stack>
              {outstandingInvoices.length > 1 && (
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Payments are applied to the oldest payable invoice first.</Typography>
              )}
            </Stack>

            {isTrialActivation && trialChannel && (
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ ...labelSx, mb: 0.75 }}>Trial delivery options</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <FormControlLabel
                    control={<Switch size="small" checked={trialDirectEnabled} disabled={trialDeliverySaving !== null} onChange={(event) => handleTrialDeliveryToggle('direct', event.target.checked)} />}
                    label={trialDeliverySaving === 'direct' ? 'Updating Direct Pass...' : 'Enable Direct Pass'}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem', fontWeight: 700 } }}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={trialSocialEnabled} disabled={trialDeliverySaving !== null} onChange={(event) => handleTrialDeliveryToggle('social', event.target.checked)} />}
                    label={trialDeliverySaving === 'social' ? 'Updating Social...' : 'Enable Social'}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem', fontWeight: 700 } }}
                  />
                  <FormControlLabel
                    control={<Switch size="small" checked={trialHlsEnabled} disabled={trialDeliverySaving !== null} onChange={(event) => handleTrialDeliveryToggle('hls', event.target.checked)} />}
                    label={trialDeliverySaving === 'hls' ? 'Updating HLS...' : 'Enable HLS'}
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.78rem', fontWeight: 700 } }}
                  />
                </Stack>
                <Typography sx={{ mt: 0.75, fontSize: '0.72rem', color: 'text.secondary' }}>
                  These choices are consolidated into one activation proforma when you choose Activate channel. Changing an option does not issue an invoice.
                </Typography>
              </Box>
            )}

            {Math.abs(prorationMultiplier - 1) > 0.001 && (
              <Alert severity="info" sx={{ mt: 1.25, borderRadius: 1 }}>
                Add-ons are prorated to the next channel billing date: {prorationRemainingDays ?? 'N/A'} of {prorationFullPeriodDays ?? 'N/A'} days ({(prorationMultiplier * 100).toFixed(0)}%).
              </Alert>
            )}
          </Box>
        </Box>

        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1.25 }}>
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>Services by channel</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Each channel keeps its own services, renewal date, and payment action.</Typography>
            </Box>
            <Button variant="outlined" size="small" startIcon={<StorefrontIcon />} onClick={onOpenStore} sx={smallBtnSx}>Add services</Button>
          </Stack>
          <Stack spacing={1.25}>
            {summary.services.channels.length === 0
              ? <Alert severity="info" sx={{ borderRadius: 1 }}>No channel services found.</Alert>
              : summary.services.channels.map((channel) => (
                <ChannelServices
                  key={channel.id}
                  channel={channel}
                  currency={currency}
                  canPay={summary.account.setupFeePaid && ['ACTIVE', 'GRACE_PERIOD', 'PAST_DUE'].includes(String(summary.account.status).toUpperCase())}
                  paying={payingChannelId === channel.id}
                  onPay={handleChannelPayment}
                />
              ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' }, gap: 2 }}>
          <Box sx={cardSx}>
            <Box sx={sectionHeaderSx}><Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Account usage</Typography><StatusChip status={summary.account.status} /></Box>
            <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              <UsageLine label="Channels" used={summary.usage.channels} limit={summary.usage.limits.channels || summary.usage.channels} />
              <UsageLine label="Outputs" used={summary.usage.used.outputs} limit={summary.usage.limits.outputs} />
              <UsageLine label="Social Stream" used={summary.usage.used.socialStream} limit={summary.usage.limits.socialStream} />
              <UsageLine label="Remapped" used={summary.usage.used.managed} limit={summary.usage.limits.managed} />
              <UsageLine label="HLS" used={summary.usage.used.hls} limit={summary.usage.limits.hls} />
              <UsageLine label="HLS+" used={summary.usage.used.hlsPlus} limit={summary.usage.limits.hlsPlus} />
            </Box>
          </Box>
          <Box sx={cardSx}>
            <Box sx={sectionHeaderSx}><Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Billing profile</Typography><CreditCardIcon sx={{ color: 'text.disabled' }} /></Box>
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box><Typography sx={labelSx}>Billing contact</Typography><Typography sx={{ fontSize: '0.82rem', mt: 0.5 }}>{summary.account.email}</Typography><Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{[summary.account.billingContact.firstName, summary.account.billingContact.lastName, summary.account.billingContact.country].filter(Boolean).join(' / ') || 'Profile incomplete'}</Typography></Box>
                <Box><Typography sx={labelSx}>Payment method</Typography><Typography sx={{ fontSize: '0.82rem', mt: 0.5 }}>Manual review</Typography><Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Payments are verified by an administrator.</Typography></Box>
              </Stack>
            </Box>
          </Box>
        </Box>

        <Box sx={cardSx}>
          <Box sx={{ ...sectionHeaderSx, gap: 1, alignItems: { xs: 'stretch', lg: 'center' }, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Recent invoices</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                {outstandingInvoices.length > 0
                  ? `${outstandingInvoices.length} unpaid · ${formatMoney(amountDueCurrency, outstandingBalance)} outstanding`
                  : 'No unpaid invoices'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ sm: 'center' }}>
              <TextField select size="small" label="Status" value={invoiceStatusFilter} onChange={(event) => setInvoiceStatusFilter(event.target.value)} sx={{ minWidth: 130 }}>
                <MenuItem value="ALL">All statuses</MenuItem>
                {invoiceStatusOptions.map((status) => <MenuItem key={status} value={status}>{status.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Service" value={invoiceServiceFilter} onChange={(event) => setInvoiceServiceFilter(event.target.value)} sx={{ minWidth: 180 }}>
                <MenuItem value="ALL">All services</MenuItem>
                {invoiceServiceOptions.map((service) => <MenuItem key={service.code} value={service.code}>{service.label}</MenuItem>)}
              </TextField>
              <Button size="small" variant="outlined" onClick={() => setInvoiceOpen(true)} sx={smallBtnSx}>View all</Button>
            </Stack>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>{['Invoice', 'Channel', 'Service(s)', 'Issued', 'Due', 'Invoice Total', 'Balance', 'Status', ''].map((h) => <TableCell key={h} sx={{ ...labelSx, py: 1 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {filteredInvoices.slice(0, 6).map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{invoice.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{invoice.channelName || 'Account-level'}</TableCell>
                    <TableCell><InvoiceServices invoice={invoice} /></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(invoice.issueDate)}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell sx={monoSx}>{formatMoney(invoice.currency, invoice.totalAmount)}</TableCell>
                    <TableCell sx={monoSx}>{formatMoney(invoice.currency, invoice.balanceDue)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.35} alignItems="flex-start">
                        <StatusChip status={invoice.status} />
                        {invoice.latestPaymentStatus && String(invoice.status || '').toUpperCase() !== 'PAID' && <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>Attempt: {invoice.latestPaymentStatus}</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{renderInvoiceActions(invoice)}</TableCell>
                  </TableRow>
                ))}
                {filteredInvoices.length === 0 && <TableRow><TableCell colSpan={9}><Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', py: 2, textAlign: 'center' }}>{summary.invoices.length ? 'No invoices match these filters.' : 'No invoices yet.'}</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Dialog open={Boolean(cancelAttemptTarget)} onClose={() => !invoiceActionId && setCancelAttemptTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 900 }}>Cancel Payment Attempt</Typography>
            <Alert severity="warning" sx={{ borderRadius: 0 }}>
              This cancels the active attempt only. The invoice and its outstanding balance remain payable.
            </Alert>
            <Typography sx={{ fontSize: '0.78rem' }}>
              {cancelAttemptTarget?.invoiceNumber} / {formatMoney(cancelAttemptTarget?.currency || 'USD', cancelAttemptTarget?.balanceDue || 0)} outstanding
            </Typography>
            <TextField
              size="small"
              label="Reason"
              value={cancelAttemptReason}
              onChange={(event) => setCancelAttemptReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelAttemptTarget(null)} disabled={invoiceActionId === cancelAttemptTarget?.id}>Keep Attempt</Button>
          <Button color="warning" variant="contained" onClick={handleCancelPaymentAttempt} disabled={invoiceActionId === cancelAttemptTarget?.id || cancelAttemptReason.trim().length < 3}>
            {invoiceActionId === cancelAttemptTarget?.id ? <CircularProgress size={16} color="inherit" /> : 'Cancel Attempt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)} PaperProps={{ sx: { width: { xs: '100%', md: 620 }, borderRadius: 0 } }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>Payment History</Typography>
            <Button size="small" variant="outlined" startIcon={invoiceActionId === -1 ? <CircularProgress size={14} /> : <DownloadIcon />} onClick={handleDownloadPaymentHistory} disabled={invoiceActionId === -1 || summary.payments.length === 0} sx={smallBtnSx}>Download all</Button>
          </Stack>
          <IconButton onClick={() => setHistoryOpen(false)} size="small" sx={{ borderRadius: 0 }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>{['Date', 'Gateway', 'Description', 'Amount', 'Status', ''].map((h) => <TableCell key={h} sx={{ ...labelSx, py: 1 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {summary.payments.map((payment: BillingPaymentSummary) => (
                  <TableRow key={payment.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{formatDate(payment.paidAt || payment.initiatedAt)}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{payment.gateway}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{payment.description || payment.transactionId || '--'}</Typography>
                      {(payment.items || []).slice(0, 3).map((item) => (
                        <Typography key={item.id} sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                          {item.description} x {item.quantity} - {formatMoney(payment.currency, item.totalAmount)}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell sx={monoSx}>{formatMoney(payment.currency, payment.amount)}</TableCell>
                    <TableCell><StatusChip status={payment.status} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Download payment receipt PDF">
                        <span>
                          <IconButton size="small" sx={{ borderRadius: 0 }} disabled={invoiceActionId === payment.id} onClick={() => handleDownloadPayment(payment)}>
                            {invoiceActionId === payment.id ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {summary.payments.length === 0 && <TableRow><TableCell colSpan={6}>No payment records yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Drawer>

      <Drawer anchor="right" open={invoiceOpen} onClose={() => setInvoiceOpen(false)} PaperProps={{ sx: { width: { xs: '100%', md: 720 }, borderRadius: 0 } }}>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>Invoice Detail</Typography><IconButton onClick={() => setInvoiceOpen(false)} size="small" sx={{ borderRadius: 0 }}><CloseIcon /></IconButton></Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ px: 2, pt: 2 }}>
          <TextField select size="small" fullWidth label="Status" value={invoiceStatusFilter} onChange={(event) => setInvoiceStatusFilter(event.target.value)}>
            <MenuItem value="ALL">All statuses</MenuItem>
            {invoiceStatusOptions.map((status) => <MenuItem key={status} value={status}>{status.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
          <TextField select size="small" fullWidth label="Service" value={invoiceServiceFilter} onChange={(event) => setInvoiceServiceFilter(event.target.value)}>
            <MenuItem value="ALL">All services</MenuItem>
            {invoiceServiceOptions.map((service) => <MenuItem key={service.code} value={service.code}>{service.label}</MenuItem>)}
          </TextField>
        </Stack>
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Box sx={cardSx}>
              <Box sx={sectionHeaderSx}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Next Invoice Preview</Typography>
                <Typography sx={{ ...monoSx, fontWeight: 800 }}>{formatMoney(currency, nextInvoiceAmount)}</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Stack spacing={0.9}>
                  {invoicePreviewLines.length > 0 ? invoicePreviewLines.map((item, index) => (
                    <Stack key={`${item.description}-${index}`} direction="row" justifyContent="space-between" spacing={2}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.description}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                          {item.quantity} x {formatMoney(currency, item.unitPrice)}
                          {item.isProrated ? ' / prorated' : ''}
                        </Typography>
                      </Box>
                      <Typography sx={monoSx}>{formatMoney(currency, item.totalAmount)}</Typography>
                    </Stack>
                  )) : (
                    <>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Recurring services</Typography><Typography sx={monoSx}>{formatMoney(currency, recurring)}</Typography></Stack>
                      <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Setup fee</Typography><Typography sx={monoSx}>{formatMoney(currency, setupFee)}</Typography></Stack>
                    </>
                  )}
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>Total</Typography>
                    <Typography sx={{ ...monoSx, fontWeight: 900 }}>{formatMoney(currency, nextInvoiceAmount)}</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>
            {filteredInvoices.map((invoice: BillingInvoiceSummary) => <Box key={invoice.id} sx={cardSx}><Box sx={sectionHeaderSx}><Box><Stack direction="row" spacing={0.75} alignItems="center"><Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>{invoice.invoiceNumber}</Typography><Chip size="small" label={invoice.channelName || 'Account-level'} variant="outlined" sx={{ borderRadius: 0, height: 20, fontSize: '0.64rem' }} /></Stack><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{invoice.title}</Typography></Box><Stack direction="row" spacing={1} alignItems="center"><StatusChip status={invoice.status} /><Typography sx={{ ...monoSx, fontWeight: 800 }}>{formatMoney(invoice.currency, invoice.totalAmount)}</Typography>{renderInvoiceActions(invoice)}</Stack></Box><Box sx={{ p: 2 }}><Stack spacing={0.75}>{invoice.items.map((item) => <Stack key={item.id} direction="row" justifyContent="space-between" spacing={2}><Typography sx={{ fontSize: '0.78rem' }}>{item.description} x {item.quantity}</Typography><Typography sx={monoSx}>{formatMoney(invoice.currency, item.totalAmount)}</Typography></Stack>)}{invoice.items.length === 0 && <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>No invoice lines recorded.</Typography>}</Stack></Box></Box>)}
            {filteredInvoices.length === 0 && <Alert severity="info" sx={{ borderRadius: 1 }}>No invoices match these filters.</Alert>}
          </Stack>
        </Box>
      </Drawer>

      <Dialog open={recurringDetailOpen} onClose={() => setRecurringDetailOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>Recurring Billing Summary</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>Grouped from active channel services.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setRecurringDetailOpen(false)} sx={{ borderRadius: 0 }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {recurringItems.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 0 }}>No recurring services are currently billed.</Alert>
          ) : (
            <Stack spacing={1}>
              {recurringItems.map((item) => (
                <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.75 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{item.label}</Typography>
                  <Typography sx={{ ...monoSx, fontWeight: 800 }}>{formatMoney(currency, item.amount)}</Typography>
                </Stack>
              ))}
              <Divider />
              <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ pt: 0.5 }}>
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 900 }}>Total</Typography>
                <Typography sx={{ ...monoSx, fontWeight: 900 }}>{formatMoney(currency, recurringItemizedTotal || recurring)}</Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '0.5px solid', borderColor: 'divider' }}>
          <Button size="small" onClick={() => setRecurringDetailOpen(false)} sx={smallBtnSx}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
